"use client"

import ArticleBox from '../ArticleBox/ArticleBox';
import { useEffect, useState, useCallback, useRef } from "react";
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { storyblokInit, apiPlugin, getStoryblokApi } from "@storyblok/react";

// ✅ Dynamically import Lottie with SSR disabled
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import blogLottie from "../../assets/bloglottie.json";
import Link from 'next/link';

storyblokInit({
    accessToken: process.env.NEXT_PUBLIC_STORYBLOK_TOKEN,
    use: [apiPlugin], // Enable API Plugin
});

function formatDateToLong(dateTimeStr) {
    const date = new Date(dateTimeStr.replace(" ", "T")); // Ensure valid ISO format
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
}

// Loading skeleton component
const CardSkeleton = () => (
    <div className="relative flex-1 p-[4px] rounded-lg bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50 w-full animate-pulse">
        <div className="h-full w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-start items-center text-center py-[40px]">
            <div className="h-8 w-3/4 bg-gray-200 rounded mb-2"></div>
            <div className="h-[95px] w-[95px] bg-gray-200 rounded-full mb-6"></div>
            <div className="h-6 w-1/2 bg-gray-200 rounded mb-4"></div>
            <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
            <div className="flex justify-between items-center mt-[69px] gap-[25px]">
                <div className="w-[38px] h-[46.69px] bg-gray-200 rounded"></div>
                <div className="h-6 w-24 bg-gray-200 rounded"></div>
            </div>
        </div>
    </div>
);

function VulnerabilityNotices({ blok }) {
    const [allVulnerabilityNotices, setAllVulnerabilityNotices] = useState([]);
    const [displayedVulnerabilityNotices, setDisplayedVulnerabilityNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const itemsPerPage = 6;
    const currentPage = useRef(1);
    const observerRef = useRef(null);
    const loadingRef = useRef(null);
    const dateInputRef = useRef(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [selectedDate, setSelectedDate] = useState("All");

    // Extract unique categories and dates for filters
    const categories = ["All", ...Array.from(new Set(allVulnerabilityNotices.map(card => {
        const cardContent = card?.content?.body;
        const hero = cardContent?.find(item => item.component === "Zero Days Hero");
        return hero?.category || null;
    }).filter(Boolean)))];
    const dates = ["All", ...Array.from(new Set(allVulnerabilityNotices.map(card => {
        const cardContent = card?.content?.body;
        const hero = cardContent?.find(item => item.component === "Zero Days Hero");
        return hero?.published_date ? (new Date(hero.published_date)).toISOString().slice(0, 10) : null;
    }).filter(Boolean)))];

    const fetchAllZeroDays = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const storyblokApi = getStoryblokApi();
            const { data } = await storyblokApi.get("cdn/stories", {
                starts_with: "zero-day/",
                per_page: 100, // Fetch a larger number of items
            });

            // Convert and sort stories
            const sortedStories = data.stories
                .map(story => {
                    const zeroDaysHero = story.content.body.find(item => item.component === "Zero Days Hero");
                    if (zeroDaysHero?.published_date) {
                        zeroDaysHero.parsedDate = new Date(Date.parse(zeroDaysHero.published_date));
                    }
                    return { ...story, zeroDaysHero };
                })
                .sort((a, b) => (b.zeroDaysHero?.parsedDate || 0) - (a.zeroDaysHero?.parsedDate || 0));

            setAllVulnerabilityNotices(sortedStories);
            // Initialize displayed items
            setDisplayedVulnerabilityNotices(sortedStories.slice(0, itemsPerPage));
        } catch (err) {
            console.error("Error fetching Zero days:", err);
            setError("Failed to load stories. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllZeroDays();
    }, [fetchAllZeroDays]);

    const loadMore = useCallback(() => {
        const nextPage = currentPage.current + 1;
        const startIndex = (nextPage - 1) * itemsPerPage;
        const endIndex = nextPage * itemsPerPage;

        if (startIndex < allVulnerabilityNotices.length) {
            const newItems = allVulnerabilityNotices.slice(startIndex, endIndex);
            setDisplayedVulnerabilityNotices(prev => [...prev, ...newItems]);
            currentPage.current = nextPage;
        }
    }, [allVulnerabilityNotices]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const lastEntry = entries[0];
                if (lastEntry.isIntersecting && !loading && displayedVulnerabilityNotices.length < allVulnerabilityNotices.length) {
                    loadMore();
                }
            },
            {
                root: null,
                rootMargin: '200px',
                threshold: [0, 0.1, 0.2]
            }
        );

        if (loadingRef.current) {
            observer.observe(loadingRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.disconnect();
            }
        };
    }, [loading, displayedVulnerabilityNotices.length, allVulnerabilityNotices.length, loadMore]);

    // Filtering logic
    const getFilteredNotices = () => {
        return allVulnerabilityNotices.filter(card => {
            const cardContent = card?.content?.body;
            const hero = cardContent?.find(item => item.component === "Zero Days Hero");
            // Search filter (by lead or subheading)
            const matchesSearch = searchTerm === "" || (
                (hero?.lead || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (hero?.subheading || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
            // Category filter
            const matchesCategory = selectedCategory === "All" || hero?.category === selectedCategory;
            // Date filter (compare YYYY-MM-DD)
            const matchesDate = selectedDate === "All" || (hero?.published_date && (new Date(hero.published_date)).toISOString().slice(0, 10) === selectedDate);
            return matchesSearch && matchesCategory && matchesDate;
        });
    };

    // Update displayed notices when filters change
    useEffect(() => {
        const filtered = getFilteredNotices();
        setDisplayedVulnerabilityNotices(filtered.slice(0, itemsPerPage));
        currentPage.current = 1;
    }, [searchTerm, selectedCategory, selectedDate, allVulnerabilityNotices]);

    return (
        <div className='m-auto w-full'>
            <div className='bg-asmPageTopBG' style={{
                backgroundSize: "cover",
                backgroundPosition: "bottom",
                backgroundRepeat: "repeat",
                margin: 'auto'
            }}>
                <div className='m-auto'>
                    <div className="relative lg:min-h-[1000px] lg:flex flex-col items-center py-8 m-auto">
                        <div className="font-aileron pt-20 lg:pt-32 font-bold max-w-7xl flex lg:flex-row flex-col text-center lg:text-start items-center justify-center lg:gap-10 gap-0">
                            <img src={blok.icon.filename} alt="Vulnerability Notices" className="lg:w-[200px] lg:h-[200px] w-[100px] h-[100px] object-cover" />
                            <div className="flex flex-col justify-center">
                                <div className="bg-custom-radial bg-clip-text text-transparent text-[48px] lg:text-[80px] p-4 lg:p-0">
                                    {blok.title}
                                </div>
                                <p className="font-lato font-normal text-[16px] lg:text-[18px] mb-8 px-[24px] xl:px-0">{blok.description}</p>
                            </div>
                        </div>
                        {/* Filter Bar */}
                        <div className="w-full max-w-7xl flex flex-col md:flex-row gap-4 md:gap-6 items-center justify-center mt-6 px-4 lg:px-0 pt-5 lg:pt-20">
                            {/* Search */}
                            <div className="w-full flex-2 relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                                    {/* Heroicons solid search icon SVG */}
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-5 focus:outline-none focus:ring-2 focus:ring-[#5A27E7]"
                                    placeholder="Search for CVE ..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </div>
                            {/* Category */}
                            <div className="w-full flex-1">
                                <select
                                    className="w-full border border-gray-300 rounded-lg px-4 py-5 focus:outline-none focus:ring-2 focus:ring-[#5A27E7]"
                                    value={selectedCategory}
                                    onChange={e => setSelectedCategory(e.target.value)}
                                >
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat === "All" ? "Categories: All" : cat}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Date Selector */}
                            <div className="w-full flex-1 flex items-center gap-2">
                                <input
                                    type="date"
                                    className="w-full border border-gray-300 rounded-lg px-4 py-5 focus:outline-none focus:ring-2 focus:ring-[#5A27E7] bg-white text-gray-900"
                                    value={selectedDate === 'All' ? '' : selectedDate}
                                    onChange={e => setSelectedDate(e.target.value || 'All')}
                                    max={new Date().toISOString().slice(0, 10)}
                                />
                                {selectedDate !== 'All' && selectedDate !== '' && (
                                    <button
                                        type="button"
                                        className="ml-2 px-3 py-2 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                                        onClick={() => setSelectedDate('All')}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        {/* End Filter Bar */}
                        <div className='lg:w-7xl lg:p-0 p-4'>
                            <div className='flex flex-col items-center text-center justify-center w-[100%] m-auto py-14'>
                                {error && (
                                    <div className="text-red-500 mb-4">
                                        {error}
                                    </div>
                                )}
                                <div className='grid lg:grid-cols-3 gap-10 w-full'>
                                    {displayedVulnerabilityNotices?.map((card, index) => {
                                        const cardContent = card?.content?.body;
                                        const { lead, subheading, published_date, publisher_icon } = cardContent?.find(item => item.component === "Zero Days Hero") || {};
                                        return (
                                            <Link
                                                href={`/zero-day/${card.slug}`}
                                                key={`zc-${card.id || index}`}
                                                className="relative flex-1 p-[4px] rounded-lg bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50 w-full"
                                            >
                                                <div className="h-[270px] w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-start items-center text-center py-[40px]">
                                                    <p className="font-lato text-[24px] !text-[#49378F] font-bold mb-2" style={{ color: "#49378F" }}>
                                                        {lead}
                                                    </p>
                                                    <div className='flex flex-row justify-center shadow-md items-center gap-[25px] mt-auto bg-[#FFF8F8] w-full p-4'>
                                                        <p className='text-sm font-lato font-normal flex flex-row items-center gap-[10px]'>
                                                            <span className='font-bold'>Severity:</span> 
                                                            <span className="p-1 bg-[#A60D08] h-[25px] w-[25px] text-white flex items-center z-10 justify-center text-sm relative  after:absolute after:top-[4px] after:right-[0px] after:translate-x-[55%] after:content-[''] after:w-[17px] after:z-0 after:h-[17px] after:bg-[#A60D08] after:rotate-45">C</span>    
                                                            <span className='ml-2'>Critical</span>
                                                        </p>
                                                        <p className='text-sm font-lato font-normal flex flex-row items-center gap-[10px]'>
                                                            <span className='font-bold'>Score:</span>
                                                            <span className=' bg-[#A60D08] text-white px-2 py-1 rounded-md'>9.8</span>
                                                        </p>
                                                    </div>
                                                   
                                                    <div className='flex justify-between items-center gap-[25px] mt-auto'>
                                                        <img src={"/cal.svg"} className='w-[38px] h-[46.69px] object-contain' alt="Calendar Icon" />
                                                        <p className='text-[24px] font-normal'>
                                                            {published_date ? formatDateToLong(published_date) : ""}
                                                        </p>
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                                {loading && (
                                    <div className="flex flex-col items-center justify-center w-full py-8">
                                        <Lottie animationData={blogLottie} className='w-32 h-32' />
                                    </div>
                                )}
                                {!loading && displayedVulnerabilityNotices.length < allVulnerabilityNotices.length && (
                                    <div ref={loadingRef} className="h-10 w-full" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VulnerabilityNotices;
